// ============================================================================
// Shopee — error classes + wire shapes + sanitiser / classifier helpers.
//
// Shopee Open Platform errors follow the shape:
//   {
//     "error": "error_auth",        // machine-readable
//     "message": "Invalid partner_id", // human-readable (sometimes echoes input)
//     "request_id": "abc123..."
//   }
//
// Note: Shopee sometimes returns HTTP 200 WITH `error` set in the body — the
// envelope is the source of truth, not the status line. `shopeeApiCall` handles
// both the non-2xx path and the "200 with error envelope" path uniformly by
// passing the HTTP status + envelope here.
//
// We classify the most common failure modes into a discriminated error class
// so callers (dispatcher, commerce sync jobs, agents) can react without
// re-parsing Shopee's documented error catalog:
//
//   - AUTH_FAILED            → partner_id / partner_key rejected. Operator
//                                needs to fix the partner credentials.
//   - TOKEN_EXPIRED          → access_token expired; dispatcher must refresh
//                                via the refresh_token before retrying.
//   - SIGNATURE_INVALID      → HMAC we sent does not match what Shopee expects.
//                                Either the partner_key is wrong OR our base
//                                string construction drifted.
//   - RATE_LIMITED           → 429 / `error_limit`. Retry after `retry-after`.
//   - SHOP_NOT_AUTHORIZED    → the shop owner has not authorised our partner
//                                app (or revoked the authorisation).
//   - ITEM_NOT_FOUND         → product / item_id unknown or deleted.
//   - ORDER_NOT_FOUND        → order_sn unknown (typo, wrong region).
//   - PRODUCT_SYNC_FAILED    → Shopee accepted the call but the async job
//                                failed (e.g. stock update partially applied).
//   - INVALID_PAYLOAD        → 400 (not token / auth related) — programmer
//                                bug; fix the caller.
//   - PROVIDER_ERROR         → everything else (5xx, unknown shape).
//   - CLOCK_DRIFT            → local clock differs from Shopee's expected
//                                window by > 5 minutes; `signShopeeRequest`
//                                throws this pre-flight so we don't burn a
//                                round-trip to learn about it.
// ============================================================================

// ============================================================================
// Wire shapes — the minimum slice of the Shopee schema we care about.
// ============================================================================

/**
 * Canonical Shopee error envelope. `error` is machine-readable
 * (e.g. `error_auth`); `message` is human-readable (sometimes echoes input —
 * always pass through `sanitizeShopeeErrorMessage` before logging).
 */
export interface ShopeeErrorEnvelope {
  error?: string
  message?: string
  request_id?: string
}

/** Shopee item shape — `/api/v2/product/get_item_base_info` subset we consume. */
export interface ShopeeItemRaw {
  item_id: number
  item_name: string
  item_sku?: string
  description?: string
  price_info?: Array<{
    currency?: string
    /** Original / undiscounted price per Shopee's docs. */
    original_price?: number
    current_price?: number
  }>
  stock_info_v2?: {
    summary_info?: {
      total_available_stock?: number
    }
    seller_stock?: Array<{ stock?: number }>
  }
  image?: {
    image_url_list?: string[]
    image_id_list?: string[]
  }
  item_status?: string
  category_id?: number
  create_time?: number
  update_time?: number
  has_model?: boolean
  models?: ShopeeModelRaw[]
}

/** Shopee variant shape — sub-SKU on a product with `has_model=true`. */
export interface ShopeeModelRaw {
  model_id: number
  model_name?: string
  model_sku?: string
  price_info?: Array<{
    original_price?: number
    current_price?: number
  }>
  stock_info_v2?: {
    summary_info?: {
      total_available_stock?: number
    }
    seller_stock?: Array<{ stock?: number }>
  }
  tier_index?: number[]
  image_info?: {
    image_url?: string
  }
}

/** `/api/v2/order/get_order_detail` subset we consume. */
export interface ShopeeOrderRaw {
  order_sn: string
  order_status?: string
  buyer_username?: string
  total_amount?: number
  currency?: string
  create_time?: number
  update_time?: number
  pay_time?: number
  tracking_number?: string
  recipient_address?: {
    name?: string
    phone?: string
    full_address?: string
    town?: string
    district?: string
    city?: string
    state?: string
    region?: string
    zipcode?: string
  }
  item_list?: Array<{
    item_id: number
    item_name?: string
    model_id?: number
    model_sku?: string
    item_sku?: string
    model_quantity_purchased?: number
    model_original_price?: number
    model_discounted_price?: number
  }>
}

/** `/api/v2/product/get_item_list` subset. */
export interface ShopeeItemListResponse {
  error?: string
  message?: string
  request_id?: string
  response?: {
    item?: Array<{ item_id: number; item_status?: string; update_time?: number }>
    total_count?: number
    has_next_page?: boolean
    next_offset?: number
  }
}

/** `/api/v2/product/get_item_base_info` subset. */
export interface ShopeeItemBaseInfoResponse {
  error?: string
  message?: string
  request_id?: string
  response?: {
    item_list?: ShopeeItemRaw[]
  }
}

/** `/api/v2/order/get_order_list` subset. */
export interface ShopeeOrderListResponse {
  error?: string
  message?: string
  request_id?: string
  response?: {
    order_list?: Array<{ order_sn: string; order_status?: string; update_time?: number }>
    more?: boolean
    next_cursor?: string
  }
}

/** `/api/v2/order/get_order_detail` subset. */
export interface ShopeeOrderDetailResponse {
  error?: string
  message?: string
  request_id?: string
  response?: {
    order_list?: ShopeeOrderRaw[]
  }
}

// ============================================================================
// ShopeeError — discriminated by `code` so callers can switch exhaustively.
// ============================================================================

export type ShopeeErrorCode =
  | 'AUTH_FAILED'
  | 'TOKEN_EXPIRED'
  | 'SIGNATURE_INVALID'
  | 'RATE_LIMITED'
  | 'SHOP_NOT_AUTHORIZED'
  | 'ITEM_NOT_FOUND'
  | 'ORDER_NOT_FOUND'
  | 'PRODUCT_SYNC_FAILED'
  | 'INVALID_PAYLOAD'
  | 'PROVIDER_ERROR'
  | 'CLOCK_DRIFT'
  | 'INVALID_INPUT'

export interface ShopeeErrorDetails {
  /** HTTP status of the underlying request, if known. */
  httpStatus?: number
  /** Raw provider message (sanitised). */
  providerMessage?: string
  /** Narrowest classifier from the `error` field (e.g. `error_auth`). */
  providerError?: string
  /** Shopee-supplied request id for support tickets. */
  requestId?: string
  /** Parsed Retry-After (ms) — only populated for RATE_LIMITED. */
  retryAfterMs?: number
}

export class ShopeeError extends Error {
  public readonly name = 'ShopeeError'
  constructor(
    public readonly code: ShopeeErrorCode,
    message: string,
    public readonly details: ShopeeErrorDetails = {},
  ) {
    super(message)
  }
}

/**
 * Dedicated error for HMAC / signature comparison failures — distinct class
 * so webhook tests can match on `instanceof` without reaching for a string
 * code.
 */
export class ShopeeSignatureError extends Error {
  public readonly name = 'ShopeeSignatureError'
  constructor(message: string) {
    super(message)
  }
}

// ============================================================================
// Helpers — classify / detect / sanitise
// ============================================================================

/**
 * Map a Shopee error envelope + HTTP status to a `ShopeeError`.
 *
 * Decision tree (ordered — first match wins):
 *   1. envelope.error === 'error_auth'        → AUTH_FAILED
 *   2. envelope.error === 'error_token'       → TOKEN_EXPIRED
 *   3. envelope.error === 'error_sign'        → SIGNATURE_INVALID
 *   4. envelope.error === 'error_limit' OR 429 → RATE_LIMITED
 *   5. envelope.error === 'error_permission'  → SHOP_NOT_AUTHORIZED
 *   6. envelope.error === 'error_not_found'   → ITEM_NOT_FOUND or ORDER_NOT_FOUND
 *      (the caller supplies `hint` when the call is order-scoped)
 *   7. HTTP 401 / 403 otherwise                → AUTH_FAILED
 *   8. HTTP 400                                → INVALID_PAYLOAD
 *   9. 404 otherwise                           → PROVIDER_ERROR
 *  10. everything else                          → PROVIDER_ERROR
 */
export function classifyShopeeError(
  httpStatus: number,
  envelope: ShopeeErrorEnvelope | null | undefined,
  retryAfterMs?: number,
  hint?: 'order' | 'item',
): ShopeeError {
  const providerError = typeof envelope?.error === 'string' ? envelope.error : ''
  const rawMessage =
    envelope?.message ??
    (providerError ? `Shopee error ${providerError}` : `Shopee API error (HTTP ${httpStatus})`)
  const message = sanitizeShopeeErrorMessage(rawMessage)
  const requestId = typeof envelope?.request_id === 'string' ? envelope.request_id : undefined

  const details: ShopeeErrorDetails = {
    httpStatus,
    providerMessage: message,
    providerError: providerError || undefined,
    requestId,
    retryAfterMs,
  }

  // 1-6. Envelope-driven classification (most specific).
  switch (providerError) {
    case 'error_auth':
      return new ShopeeError('AUTH_FAILED', message, details)
    case 'error_token':
      // Shopee uses `error_token` for both "expired" + "invalid". Caller should
      // try to refresh via the refresh_token; if THAT also fails with the
      // same code, the dispatcher surfaces re-auth UX.
      return new ShopeeError('TOKEN_EXPIRED', message, details)
    case 'error_sign':
      return new ShopeeError('SIGNATURE_INVALID', message, details)
    case 'error_limit':
      return new ShopeeError('RATE_LIMITED', message, details)
    case 'error_permission':
    case 'error_shop':
      return new ShopeeError('SHOP_NOT_AUTHORIZED', message, details)
    case 'error_not_found':
      return new ShopeeError(
        hint === 'order' ? 'ORDER_NOT_FOUND' : 'ITEM_NOT_FOUND',
        message,
        details,
      )
  }

  // 7. HTTP-level auth rejection.
  if (httpStatus === 401 || httpStatus === 403) {
    return new ShopeeError('AUTH_FAILED', message, details)
  }
  // 4b. 429 without the envelope classifier.
  if (httpStatus === 429) {
    return new ShopeeError('RATE_LIMITED', message, details)
  }
  // 8. Generic 400 → invalid payload.
  if (httpStatus === 400) {
    return new ShopeeError('INVALID_PAYLOAD', message, details)
  }
  // 9. 404 without the envelope classifier.
  if (httpStatus === 404) {
    return new ShopeeError(
      hint === 'order' ? 'ORDER_NOT_FOUND' : 'ITEM_NOT_FOUND',
      message,
      details,
    )
  }
  return new ShopeeError('PROVIDER_ERROR', message, details)
}

/**
 * Parse a `Retry-After` header value (either delta-seconds or HTTP-date) and
 * return milliseconds. Returns `undefined` on unparseable input so callers
 * can fall back to a default backoff strategy.
 */
export function parseShopeeRetryAfterMs(
  headerValue: string | null | undefined,
): number | undefined {
  if (!headerValue) return undefined
  const trimmed = headerValue.trim()
  if (trimmed.length === 0) return undefined
  const asNumber = Number(trimmed)
  if (Number.isFinite(asNumber) && asNumber >= 0) {
    return Math.round(asNumber * 1000)
  }
  const parsed = Date.parse(trimmed)
  if (Number.isFinite(parsed)) {
    const diff = parsed - Date.now()
    return diff > 0 ? diff : 0
  }
  return undefined
}

// ============================================================================
// Secret sanitisation
// ============================================================================

/**
 * Strip Shopee-shaped secrets out of an error / log string before it leaves
 * the adapter. Patterns mirror `scrubProviderError` in
 * `apps/api/src/services/ai-agent-processor.ts` and add Shopee-specific shapes:
 *
 *   - `partner_key=...`                — Shopee partner HMAC key (critical)
 *   - `access_token=...`               — shop-scoped access token
 *   - `refresh_token=...`              — long-lived refresh token (30d)
 *   - `sign=...` query params          — the HMAC signature itself
 *   - Long hex sequences (40+ chars)   — signatures / fingerprints
 *   - RFC 5246 cert fingerprints       — `SHA256 Fingerprint=aa:bb:...`
 *
 * Patterns intentionally redact aggressively — false positives on an error
 * string are cheap; a leaked `partner_key` or `access_token` in Workers tail
 * logs is catastrophic (Shopee partner keys authorise every API call for
 * every linked shop).
 */
export function sanitizeShopeeErrorMessage(raw: string): string {
  if (!raw) return raw
  let out = raw
  // Query-string style token leaks.
  out = out.replace(/(partner[_-]?key=)[^&\s"']+/gi, '$1REDACTED')
  out = out.replace(/(access[_-]?token=)[^&\s"']+/gi, '$1REDACTED')
  out = out.replace(/(refresh[_-]?token=)[^&\s"']+/gi, '$1REDACTED')
  out = out.replace(/(sign=)[^&\s"']+/gi, '$1REDACTED')
  // `Authorization: Bearer <token>` / `Bearer <token>` — Shopee doesn't use
  // bearer, but bot libraries sometimes stringify the header.
  out = out.replace(/(Bearer\s+)[A-Za-z0-9._~+/=\-|]+/gi, '$1REDACTED')
  // RFC 5246 cert fingerprints: `SHA256 Fingerprint=aa:bb:cc:...` (32 hex
  // byte pairs separated by `:`, any case).
  out = out.replace(
    /(SHA[-_]?(?:1|256|384|512)\s+Fingerprint\s*=\s*)[0-9A-Fa-f:]{20,}/gi,
    '$1REDACTED',
  )
  // Long hex sequences (40+ chars) — HMAC-SHA256 is 64 chars, so 40+ catches
  // signatures without swallowing short request IDs / order_sn values.
  out = out.replace(/\b[0-9a-fA-F]{40,}\b/g, 'REDACTED')
  // Bare base64 / JWT-like blobs 40+ chars.
  out = out.replace(/[A-Za-z0-9+/_\-]{40,}={0,2}/g, (match) => {
    // Don't scrub plain hex < 64 (order_sn, request_id) — the hex rule above
    // already bounds at 40+, but this guard lets us keep short descriptive
    // strings that slip through.
    if (/^[0-9a-f]+$/.test(match) && match.length <= 32) return match
    return 'REDACTED'
  })
  return out
}

// ============================================================================
// Status mapping — Shopee → canonical commerce
// ============================================================================

/**
 * Map Shopee's wire-level order status strings to the canonical
 * `CommerceOrderStatus` alphabet.
 *
 * Shopee status reference:
 *   UNPAID              → pending   (buyer hasn't paid yet)
 *   READY_TO_SHIP       → ready     (paid, seller must fulfil)
 *   PROCESSED           → ready     (seller acknowledged but not shipped)
 *   RETRY_SHIP          → ready     (first ship failed, awaiting retry)
 *   SHIPPED             → shipped   (handed to carrier)
 *   TO_CONFIRM_RECEIVE  → shipped   (in transit, waiting for buyer)
 *   COMPLETED           → delivered (buyer confirmed / auto-complete)
 *   CANCELLED           → cancelled
 *   IN_CANCEL           → cancelled (cancellation request pending)
 *   INVOICE_PENDING     → cancelled (invoice flow abandoned — rare)
 *   TO_RETURN           → cancelled (return initiated, treat as cancelled)
 *
 * Round 1 H3 fix: unknown / missing values default to `pending` — NOT
 * `cancelled`. Shopee periodically adds new status strings (e.g.
 * `UNPAID_CONFIRM`); mapping them to `cancelled` would silently release
 * inventory for a live order, which is the worst-possible failure mode
 * for an inventory sync. `pending` is safe because downstream sync jobs
 * idempotently re-read the order later and the next status change will
 * correct the record. Ops should watch logs for unmapped values via
 * `console.warn` below.
 */
export function mapShopeeOrderStatus(
  shopeeStatus: string | null | undefined,
): 'pending' | 'ready' | 'shipped' | 'delivered' | 'cancelled' {
  if (typeof shopeeStatus !== 'string' || shopeeStatus.length === 0) {
    return 'pending'
  }
  switch (shopeeStatus.toUpperCase()) {
    case 'UNPAID':
      return 'pending'
    case 'READY_TO_SHIP':
    case 'PROCESSED':
    case 'RETRY_SHIP':
      return 'ready'
    case 'SHIPPED':
    case 'TO_CONFIRM_RECEIVE':
      return 'shipped'
    case 'COMPLETED':
      return 'delivered'
    case 'CANCELLED':
    case 'IN_CANCEL':
    case 'INVOICE_PENDING':
    case 'TO_RETURN':
      return 'cancelled'
    default:
      console.warn(
        `[shopee/types] unmapped Shopee order status "${shopeeStatus}" — defaulting to pending`,
      )
      return 'pending'
  }
}

/**
 * Inverse of `mapShopeeOrderStatus` — convert a canonical status filter into
 * the Shopee wire-level status string(s) used by `/api/v2/order/get_order_list`.
 * Shopee accepts exactly ONE status per request (not a list), so the fetch
 * layer fans out N requests if the caller supplies multiple statuses.
 */
export function canonicalToShopeeStatus(
  status: 'pending' | 'ready' | 'shipped' | 'delivered' | 'cancelled',
): string {
  switch (status) {
    case 'pending':
      return 'UNPAID'
    case 'ready':
      return 'READY_TO_SHIP'
    case 'shipped':
      return 'SHIPPED'
    case 'delivered':
      return 'COMPLETED'
    case 'cancelled':
      return 'CANCELLED'
  }
}
